/**
 * Sprint S13.1.B.1 — Step 3: smoking + alcohol consumption.
 *
 * Both fields default к "prefer_not_to_say" per Phase E schema. zod-validated
 * к match backend Pydantic enum literals.
 */
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/Button";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { FieldSelect } from "@/components/onboarding/fields/FieldSelect";


const schema = z.object({
  smoking_status: z.enum(["never", "former", "current", "prefer_not_to_say"]),
  alcohol_consumption: z.enum([
    "none",
    "occasional",
    "weekly",
    "daily",
    "prefer_not_to_say",
  ]),
});

type LifestyleForm = z.infer<typeof schema>;


const SMOKING_VALUES = ["never", "former", "current", "prefer_not_to_say"] as const;
const ALCOHOL_VALUES = [
  "none",
  "occasional",
  "weekly",
  "daily",
  "prefer_not_to_say",
] as const;


export function Step3Lifestyle() {
  const { t } = useTranslation();
  const data = useOnboardingStore((s) => s.data);
  const setData = useOnboardingStore((s) => s.setData);
  const nextStep = useOnboardingStore((s) => s.nextStep);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LifestyleForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      smoking_status: data.smoking_status,
      alcohol_consumption: data.alcohol_consumption,
    },
  });

  const onSubmit = (values: LifestyleForm) => {
    setData(values);
    nextStep();
  };

  const smokingOptions = SMOKING_VALUES.map((v) => ({
    value: v,
    label: t(`onboarding.step.lifestyle.smoking.${v}`),
  }));
  const alcoholOptions = ALCOHOL_VALUES.map((v) => ({
    value: v,
    label: t(`onboarding.step.lifestyle.alcohol.${v}`),
  }));

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <h2
          id="step-heading"
          tabIndex={-1}
          className="mb-2 text-xl font-semibold text-navy-deep sm:text-2xl"
        >
          {t("onboarding.step.lifestyle.title")}
        </h2>
        <p className="text-[13px] text-taupe sm:text-[14px]">
          {t("onboarding.step.lifestyle.subtitle")}
        </p>
      </div>

      <FieldSelect
        label={t("onboarding.step.lifestyle.smoking_label")}
        hint={t("onboarding.step.lifestyle.smoking_hint")}
        options={smokingOptions}
        registration={register("smoking_status")}
        error={errors.smoking_status?.message}
      />

      <FieldSelect
        label={t("onboarding.step.lifestyle.alcohol_label")}
        hint={t("onboarding.step.lifestyle.alcohol_hint")}
        options={alcoholOptions}
        registration={register("alcohol_consumption")}
        error={errors.alcohol_consumption?.message}
      />

      <div className="pt-2">
        <Button type="submit" data-onboarding-next className="w-full sm:w-auto">
          {t("onboarding.nav.next")}
        </Button>
      </div>
    </form>
  );
}
